import { sanitizeHtml } from "../utils/sanitize";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Slider } from '../components/ui/slider';
import { 
  Mail, Save, Eye, Palette, Code, Settings, RefreshCw, 
  Type, Image, AlignLeft, Minus, Square, Link2, 
  Phone, MapPin, Clock, Facebook, Twitter, Linkedin, Instagram,
  GripVertical, Trash2, Copy, ChevronUp, ChevronDown, Plus,
  Wand2, Layout, Layers, Sparkles, Monitor, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

// Pre-built email block components
const EMAIL_BLOCKS = {
  header_logo: {
    id: 'header_logo',
    type: 'header_logo',
    label: 'Logo Header',
    icon: Image,
    category: 'header',
    defaultProps: {
      logoUrl: '',
      logoWidth: 150,
      backgroundColor: '#ffffff',
      padding: 30,
      alignment: 'center'
    }
  },
  header_branded: {
    id: 'header_branded',
    type: 'header_branded',
    label: 'Branded Header',
    icon: Palette,
    category: 'header',
    defaultProps: {
      title: 'Company Name',
      subtitle: '',
      backgroundColor: '#0078d4',
      textColor: '#ffffff',
      padding: 40,
      fontSize: 28
    }
  },
  text_block: {
    id: 'text_block',
    type: 'text_block',
    label: 'Text Block',
    icon: Type,
    category: 'content',
    defaultProps: {
      content: 'Enter your text here...',
      fontSize: 16,
      textColor: '#333333',
      backgroundColor: '#ffffff',
      padding: 20,
      alignment: 'left'
    }
  },
  divider: {
    id: 'divider',
    type: 'divider',
    label: 'Divider',
    icon: Minus,
    category: 'layout',
    defaultProps: {
      color: '#e5e5e5',
      thickness: 1,
      width: 100,
      margin: 20
    }
  },
  spacer: {
    id: 'spacer',
    type: 'spacer',
    label: 'Spacer',
    icon: Square,
    category: 'layout',
    defaultProps: {
      height: 30
    }
  },
  button: {
    id: 'button',
    type: 'button',
    label: 'Button',
    icon: Link2,
    category: 'content',
    defaultProps: {
      text: 'Click Here',
      url: '#',
      backgroundColor: '#0078d4',
      textColor: '#ffffff',
      borderRadius: 5,
      padding: 15,
      alignment: 'center'
    }
  },
  image: {
    id: 'image',
    type: 'image',
    label: 'Image',
    icon: Image,
    category: 'content',
    defaultProps: {
      src: '',
      alt: 'Image',
      width: 100,
      alignment: 'center',
      padding: 10
    }
  },
  contact_info: {
    id: 'contact_info',
    type: 'contact_info',
    label: 'Contact Info',
    icon: Phone,
    category: 'footer',
    defaultProps: {
      email: 'support@company.com',
      phone: '',
      address: '',
      backgroundColor: '#f5f5f5',
      textColor: '#666666',
      padding: 25
    }
  },
  social_links: {
    id: 'social_links',
    type: 'social_links',
    label: 'Social Links',
    icon: Facebook,
    category: 'footer',
    defaultProps: {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      iconSize: 24,
      iconColor: '#666666',
      backgroundColor: '#ffffff',
      padding: 20
    }
  },
  footer_simple: {
    id: 'footer_simple',
    type: 'footer_simple',
    label: 'Simple Footer',
    icon: AlignLeft,
    category: 'footer',
    defaultProps: {
      text: '© 2026 Company Name. All rights reserved.',
      backgroundColor: '#333333',
      textColor: '#cccccc',
      padding: 25,
      fontSize: 12
    }
  }
};

// Render block to HTML
const renderBlockToHtml = (block, branding) => {
  const props = block.props || {};
  
  switch (block.type) {
    case 'header_logo':
      return `<div style="background-color: ${props.backgroundColor}; padding: ${props.padding}px; text-align: ${props.alignment};">
        ${props.logoUrl ? `<img src="${props.logoUrl}" alt="Logo" style="max-width: ${props.logoWidth}px; height: auto;" />` : `<div style="color: #999; font-size: 14px;">[Logo Placeholder]</div>`}
      </div>`;
    
    case 'header_branded':
      return `<div style="background-color: ${props.backgroundColor}; color: ${props.textColor}; padding: ${props.padding}px; text-align: center;">
        <h1 style="margin: 0; font-size: ${props.fontSize}px; font-weight: bold;">${props.title || branding.company_name}</h1>
        ${props.subtitle ? `<p style="margin: 10px 0 0 0; opacity: 0.9;">${props.subtitle}</p>` : ''}
      </div>`;
    
    case 'text_block':
      return `<div style="background-color: ${props.backgroundColor}; padding: ${props.padding}px;">
        <p style="margin: 0; font-size: ${props.fontSize}px; color: ${props.textColor}; text-align: ${props.alignment}; line-height: 1.6;">${props.content}</p>
      </div>`;
    
    case 'divider':
      return `<div style="padding: ${props.margin}px 0; text-align: center;">
        <hr style="border: none; border-top: ${props.thickness}px solid ${props.color}; width: ${props.width}%; margin: 0 auto;" />
      </div>`;
    
    case 'spacer':
      return `<div style="height: ${props.height}px;"></div>`;
    
    case 'button':
      return `<div style="padding: 15px 0; text-align: ${props.alignment};">
        <a href="${props.url}" style="display: inline-block; background-color: ${props.backgroundColor}; color: ${props.textColor}; padding: ${props.padding}px ${props.padding * 2}px; text-decoration: none; border-radius: ${props.borderRadius}px; font-weight: 600;">${props.text}</a>
      </div>`;
    
    case 'image':
      return `<div style="padding: ${props.padding}px; text-align: ${props.alignment};">
        ${props.src ? `<img src="${props.src}" alt="${props.alt}" style="max-width: ${props.width}%; height: auto;" />` : `<div style="background: #f0f0f0; padding: 40px; color: #999;">[Image Placeholder]</div>`}
      </div>`;
    
    case 'contact_info':
      return `<div style="background-color: ${props.backgroundColor}; padding: ${props.padding}px; text-align: center;">
        ${props.email ? `<p style="margin: 0 0 8px 0; color: ${props.textColor};"><a href="mailto:${props.email}" style="color: ${props.textColor};">${props.email}</a></p>` : ''}
        ${props.phone ? `<p style="margin: 0 0 8px 0; color: ${props.textColor};">${props.phone}</p>` : ''}
        ${props.address ? `<p style="margin: 0; color: ${props.textColor}; font-size: 12px;">${props.address}</p>` : ''}
      </div>`;
    
    case 'social_links':
      const socialIcons = [];
      if (props.facebook) socialIcons.push(`<a href="${props.facebook}" style="display: inline-block; margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/32/733/733547.png" width="${props.iconSize}" alt="Facebook" /></a>`);
      if (props.twitter) socialIcons.push(`<a href="${props.twitter}" style="display: inline-block; margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/32/733/733579.png" width="${props.iconSize}" alt="Twitter" /></a>`);
      if (props.linkedin) socialIcons.push(`<a href="${props.linkedin}" style="display: inline-block; margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/32/733/733561.png" width="${props.iconSize}" alt="LinkedIn" /></a>`);
      if (props.instagram) socialIcons.push(`<a href="${props.instagram}" style="display: inline-block; margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/32/733/733558.png" width="${props.iconSize}" alt="Instagram" /></a>`);
      return `<div style="background-color: ${props.backgroundColor}; padding: ${props.padding}px; text-align: center;">
        ${socialIcons.length > 0 ? socialIcons.join('') : '<span style="color: #999; font-size: 12px;">[Social Links]</span>'}
      </div>`;
    
    case 'footer_simple':
      return `<div style="background-color: ${props.backgroundColor}; padding: ${props.padding}px; text-align: center;">
        <p style="margin: 0; color: ${props.textColor}; font-size: ${props.fontSize}px;">${props.text}</p>
      </div>`;
    
    default:
      return '';
  }
};

// Block Editor Panel
const BlockEditor = ({ block, onUpdate, onDelete }) => {
  if (!block) return null;
  
  const props = block.props || {};
  const blockDef = EMAIL_BLOCKS[block.type];
  
  const updateProp = (key, value) => {
    onUpdate({ ...block, props: { ...props, [key]: value } });
  };
  
  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {blockDef && <blockDef.icon className="h-4 w-4 text-blue-600" />}
          <span className="font-semibold text-sm">{blockDef?.label || block.type}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-500 hover:text-red-700 h-7">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      
      <div className="space-y-3">
        {/* Header Logo */}
        {block.type === 'header_logo' && (
          <>
            <div>
              <Label className="text-xs">Logo URL</Label>
              <Input value={props.logoUrl || ''} onChange={(e) => updateProp('logoUrl', e.target.value)} placeholder="https://..." className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Logo Width (px)</Label>
              <Slider value={[props.logoWidth || 150]} onValueChange={([v]) => updateProp('logoWidth', v)} min={50} max={300} step={10} />
              <span className="text-xs text-gray-500">{props.logoWidth}px</span>
            </div>
            <div>
              <Label className="text-xs">Background Color</Label>
              <div className="flex gap-2">
                <Input type="color" value={props.backgroundColor} onChange={(e) => updateProp('backgroundColor', e.target.value)} className="w-12 h-8 p-1" />
                <Input value={props.backgroundColor} onChange={(e) => updateProp('backgroundColor', e.target.value)} className="h-8 text-sm flex-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Alignment</Label>
              <Select value={props.alignment} onValueChange={(v) => updateProp('alignment', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        
        {/* Header Branded */}
        {block.type === 'header_branded' && (
          <>
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={props.title || ''} onChange={(e) => updateProp('title', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Subtitle</Label>
              <Input value={props.subtitle || ''} onChange={(e) => updateProp('subtitle', e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Background</Label>
                <div className="flex gap-1">
                  <Input type="color" value={props.backgroundColor} onChange={(e) => updateProp('backgroundColor', e.target.value)} className="w-10 h-8 p-1" />
                  <Input value={props.backgroundColor} onChange={(e) => updateProp('backgroundColor', e.target.value)} className="h-8 text-xs flex-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Text Color</Label>
                <div className="flex gap-1">
                  <Input type="color" value={props.textColor} onChange={(e) => updateProp('textColor', e.target.value)} className="w-10 h-8 p-1" />
                  <Input value={props.textColor} onChange={(e) => updateProp('textColor', e.target.value)} className="h-8 text-xs flex-1" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Font Size: {props.fontSize}px</Label>
              <Slider value={[props.fontSize || 28]} onValueChange={([v]) => updateProp('fontSize', v)} min={18} max={48} step={2} />
            </div>
          </>
        )}
        
        {/* Text Block */}
        {block.type === 'text_block' && (
          <>
            <div>
              <Label className="text-xs">Content</Label>
              <Textarea value={props.content || ''} onChange={(e) => updateProp('content', e.target.value)} rows={3} className="text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Text Color</Label>
                <div className="flex gap-1">
                  <Input type="color" value={props.textColor} onChange={(e) => updateProp('textColor', e.target.value)} className="w-10 h-8 p-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Font Size</Label>
                <Input type="number" value={props.fontSize} onChange={(e) => updateProp('fontSize', parseInt(e.target.value))} className="h-8 text-sm" min={10} max={32} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Alignment</Label>
              <Select value={props.alignment} onValueChange={(v) => updateProp('alignment', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        
        {/* Divider */}
        {block.type === 'divider' && (
          <>
            <div>
              <Label className="text-xs">Color</Label>
              <div className="flex gap-2">
                <Input type="color" value={props.color} onChange={(e) => updateProp('color', e.target.value)} className="w-12 h-8 p-1" />
                <Input value={props.color} onChange={(e) => updateProp('color', e.target.value)} className="h-8 text-sm flex-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Width: {props.width}%</Label>
              <Slider value={[props.width || 100]} onValueChange={([v]) => updateProp('width', v)} min={20} max={100} step={5} />
            </div>
            <div>
              <Label className="text-xs">Thickness: {props.thickness}px</Label>
              <Slider value={[props.thickness || 1]} onValueChange={([v]) => updateProp('thickness', v)} min={1} max={5} step={1} />
            </div>
          </>
        )}
        
        {/* Spacer */}
        {block.type === 'spacer' && (
          <div>
            <Label className="text-xs">Height: {props.height}px</Label>
            <Slider value={[props.height || 30]} onValueChange={([v]) => updateProp('height', v)} min={10} max={100} step={5} />
          </div>
        )}
        
        {/* Button */}
        {block.type === 'button' && (
          <>
            <div>
              <Label className="text-xs">Button Text</Label>
              <Input value={props.text || ''} onChange={(e) => updateProp('text', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Link URL</Label>
              <Input value={props.url || ''} onChange={(e) => updateProp('url', e.target.value)} className="h-8 text-sm" placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Button Color</Label>
                <Input type="color" value={props.backgroundColor} onChange={(e) => updateProp('backgroundColor', e.target.value)} className="w-full h-8 p-1" />
              </div>
              <div>
                <Label className="text-xs">Text Color</Label>
                <Input type="color" value={props.textColor} onChange={(e) => updateProp('textColor', e.target.value)} className="w-full h-8 p-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Border Radius: {props.borderRadius}px</Label>
              <Slider value={[props.borderRadius || 5]} onValueChange={([v]) => updateProp('borderRadius', v)} min={0} max={25} step={1} />
            </div>
          </>
        )}
        
        {/* Image */}
        {block.type === 'image' && (
          <>
            <div>
              <Label className="text-xs">Image URL</Label>
              <Input value={props.src || ''} onChange={(e) => updateProp('src', e.target.value)} className="h-8 text-sm" placeholder="https://..." />
            </div>
            <div>
              <Label className="text-xs">Alt Text</Label>
              <Input value={props.alt || ''} onChange={(e) => updateProp('alt', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Width: {props.width}%</Label>
              <Slider value={[props.width || 100]} onValueChange={([v]) => updateProp('width', v)} min={20} max={100} step={5} />
            </div>
          </>
        )}
        
        {/* Contact Info */}
        {block.type === 'contact_info' && (
          <>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={props.email || ''} onChange={(e) => updateProp('email', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={props.phone || ''} onChange={(e) => updateProp('phone', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input value={props.address || ''} onChange={(e) => updateProp('address', e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Background</Label>
                <Input type="color" value={props.backgroundColor} onChange={(e) => updateProp('backgroundColor', e.target.value)} className="w-full h-8 p-1" />
              </div>
              <div>
                <Label className="text-xs">Text Color</Label>
                <Input type="color" value={props.textColor} onChange={(e) => updateProp('textColor', e.target.value)} className="w-full h-8 p-1" />
              </div>
            </div>
          </>
        )}
        
        {/* Social Links */}
        {block.type === 'social_links' && (
          <>
            <div>
              <Label className="text-xs">Facebook URL</Label>
              <Input value={props.facebook || ''} onChange={(e) => updateProp('facebook', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Twitter URL</Label>
              <Input value={props.twitter || ''} onChange={(e) => updateProp('twitter', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">LinkedIn URL</Label>
              <Input value={props.linkedin || ''} onChange={(e) => updateProp('linkedin', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Instagram URL</Label>
              <Input value={props.instagram || ''} onChange={(e) => updateProp('instagram', e.target.value)} className="h-8 text-sm" />
            </div>
          </>
        )}
        
        {/* Footer Simple */}
        {block.type === 'footer_simple' && (
          <>
            <div>
              <Label className="text-xs">Footer Text</Label>
              <Textarea value={props.text || ''} onChange={(e) => updateProp('text', e.target.value)} rows={2} className="text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Background</Label>
                <Input type="color" value={props.backgroundColor} onChange={(e) => updateProp('backgroundColor', e.target.value)} className="w-full h-8 p-1" />
              </div>
              <div>
                <Label className="text-xs">Text Color</Label>
                <Input type="color" value={props.textColor} onChange={(e) => updateProp('textColor', e.target.value)} className="w-full h-8 p-1" />
              </div>
            </div>
          </>
        )}
        
        {/* Common padding control */}
        {props.padding !== undefined && (
          <div>
            <Label className="text-xs">Padding: {props.padding}px</Label>
            <Slider value={[props.padding]} onValueChange={([v]) => updateProp('padding', v)} min={0} max={60} step={5} />
          </div>
        )}
      </div>
    </div>
  );
};

export default function EmailBranding() {
  const [branding, setBranding] = useState({
    company_name: 'CipherOne',
    company_logo_url: '',
    primary_color: '#0078d4',
    support_email: 'support@cipherone.us',
    support_phone: '',
    company_address: '',
    use_custom_branding: false,
    template_blocks: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop' or 'mobile'
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'code'
  const [headerHtml, setHeaderHtml] = useState('');
  const [footerHtml, setFooterHtml] = useState('');
  const iframeRef = useRef(null);

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    setLoading(true);
    try {
      const response = await api.get('/email-branding');
      const data = response.data;
      setBranding({
        ...data,
        template_blocks: data.template_blocks || []
      });
      setHeaderHtml(data.header_html || '');
      setFooterHtml(data.footer_html || '');
    } catch (error) {
      console.error('Failed to load branding:', error);
      toast.error('Failed to load email branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saveData = {
        ...branding,
        header_html: editorMode === 'code' ? headerHtml : generateHeaderHtml(),
        footer_html: editorMode === 'code' ? footerHtml : generateFooterHtml()
      };
      await api.put('/email-branding', saveData);
      toast.success('Email branding settings saved successfully');
    } catch (error) {
      console.error('Failed to save branding:', error);
      toast.error('Failed to save email branding settings');
    } finally {
      setSaving(false);
    }
  };

  const generateHeaderHtml = () => {
    const headerBlocks = branding.template_blocks.filter(b => 
      EMAIL_BLOCKS[b.type]?.category === 'header'
    );
    return headerBlocks.map(b => renderBlockToHtml(b, branding)).join('\n');
  };

  const generateFooterHtml = () => {
    const footerBlocks = branding.template_blocks.filter(b => 
      EMAIL_BLOCKS[b.type]?.category === 'footer'
    );
    return footerBlocks.map(b => renderBlockToHtml(b, branding)).join('\n');
  };

  const generateFullEmailHtml = useCallback(() => {
    const blocks = branding.template_blocks || [];
    const blocksHtml = blocks.map(b => renderBlockToHtml(b, branding)).join('\n');
    
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Email Preview</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: Arial, sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <tr>
            <td>
              ${blocksHtml || '<div style="padding: 60px 40px; text-align: center; color: #999;"><p>Drag blocks here to build your email template</p></div>'}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  }, [branding]);

  const handlePreview = async () => {
    setLoadingPreview(true);
    setPreviewDialogOpen(true);
    try {
      // Generate preview locally
      const html = generateFullEmailHtml();
      setPreviewHtml(html);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    // srcDoc handles the preview rendering directly
  }, [previewHtml]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const { source, destination, draggableId } = result;
    
    // Adding new block from palette
    if (source.droppableId === 'block-palette') {
      const blockType = draggableId.replace('palette-', '');
      const blockDef = EMAIL_BLOCKS[blockType];
      if (blockDef) {
        const newBlock = {
          id: `${blockType}_${Date.now()}`,
          type: blockType,
          props: { ...blockDef.defaultProps }
        };
        const newBlocks = [...branding.template_blocks];
        newBlocks.splice(destination.index, 0, newBlock);
        setBranding({ ...branding, template_blocks: newBlocks });
        setSelectedBlockIndex(destination.index);
      }
      return;
    }
    
    // Reordering existing blocks
    if (source.droppableId === 'template-canvas' && destination.droppableId === 'template-canvas') {
      const newBlocks = Array.from(branding.template_blocks);
      const [removed] = newBlocks.splice(source.index, 1);
      newBlocks.splice(destination.index, 0, removed);
      setBranding({ ...branding, template_blocks: newBlocks });
      setSelectedBlockIndex(destination.index);
    }
  };

  const updateBlock = (updatedBlock) => {
    if (selectedBlockIndex === null) return;
    const newBlocks = [...branding.template_blocks];
    newBlocks[selectedBlockIndex] = updatedBlock;
    setBranding({ ...branding, template_blocks: newBlocks });
  };

  const deleteBlock = () => {
    if (selectedBlockIndex === null) return;
    const newBlocks = branding.template_blocks.filter((_, i) => i !== selectedBlockIndex);
    setBranding({ ...branding, template_blocks: newBlocks });
    setSelectedBlockIndex(null);
  };

  const moveBlock = (direction) => {
    if (selectedBlockIndex === null) return;
    const newIndex = direction === 'up' ? selectedBlockIndex - 1 : selectedBlockIndex + 1;
    if (newIndex < 0 || newIndex >= branding.template_blocks.length) return;
    
    const newBlocks = [...branding.template_blocks];
    const [removed] = newBlocks.splice(selectedBlockIndex, 1);
    newBlocks.splice(newIndex, 0, removed);
    setBranding({ ...branding, template_blocks: newBlocks });
    setSelectedBlockIndex(newIndex);
  };

  const addPresetTemplate = (preset) => {
    let blocks = [];
    switch (preset) {
      case 'modern':
        blocks = [
          { id: 'header_1', type: 'header_branded', props: { ...EMAIL_BLOCKS.header_branded.defaultProps, backgroundColor: branding.primary_color } },
          { id: 'spacer_1', type: 'spacer', props: { height: 30 } },
          { id: 'text_1', type: 'text_block', props: { ...EMAIL_BLOCKS.text_block.defaultProps, content: 'Hello,\n\nThank you for reaching out to us. We have received your message and will get back to you shortly.' } },
          { id: 'divider_1', type: 'divider', props: { ...EMAIL_BLOCKS.divider.defaultProps } },
          { id: 'contact_1', type: 'contact_info', props: { ...EMAIL_BLOCKS.contact_info.defaultProps, email: branding.support_email, phone: branding.support_phone, address: branding.company_address } },
          { id: 'footer_1', type: 'footer_simple', props: { ...EMAIL_BLOCKS.footer_simple.defaultProps, text: `© ${new Date().getFullYear()} ${branding.company_name}. All rights reserved.` } }
        ];
        break;
      case 'minimal':
        blocks = [
          { id: 'header_1', type: 'header_logo', props: { ...EMAIL_BLOCKS.header_logo.defaultProps, logoUrl: branding.company_logo_url } },
          { id: 'text_1', type: 'text_block', props: { ...EMAIL_BLOCKS.text_block.defaultProps } },
          { id: 'footer_1', type: 'footer_simple', props: { ...EMAIL_BLOCKS.footer_simple.defaultProps, backgroundColor: '#f5f5f5', textColor: '#666666' } }
        ];
        break;
      case 'professional':
        blocks = [
          { id: 'header_1', type: 'header_branded', props: { ...EMAIL_BLOCKS.header_branded.defaultProps, backgroundColor: '#1a1a2e', title: branding.company_name } },
          { id: 'spacer_1', type: 'spacer', props: { height: 20 } },
          { id: 'text_1', type: 'text_block', props: { ...EMAIL_BLOCKS.text_block.defaultProps, content: 'Dear Valued Customer,\n\nWe appreciate your business and are committed to providing you with exceptional service.' } },
          { id: 'button_1', type: 'button', props: { ...EMAIL_BLOCKS.button.defaultProps, text: 'View Details', backgroundColor: branding.primary_color } },
          { id: 'divider_1', type: 'divider', props: { ...EMAIL_BLOCKS.divider.defaultProps, width: 80 } },
          { id: 'social_1', type: 'social_links', props: { ...EMAIL_BLOCKS.social_links.defaultProps } },
          { id: 'contact_1', type: 'contact_info', props: { ...EMAIL_BLOCKS.contact_info.defaultProps, email: branding.support_email } },
          { id: 'footer_1', type: 'footer_simple', props: { ...EMAIL_BLOCKS.footer_simple.defaultProps, backgroundColor: '#1a1a2e' } }
        ];
        break;
      default:
        return;
    }
    setBranding({ ...branding, template_blocks: blocks });
    setSelectedBlockIndex(null);
    toast.success(`${preset.charAt(0).toUpperCase() + preset.slice(1)} template applied`);
  };

  if (loading) {
    return (
      <div className="co-page co-scope co-settings flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="co-page co-scope co-settings space-y-4" data-testid="email-branding-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Mail className="h-7 w-7 text-blue-600" />
            Email Branding & Templates
          </h1>
          <p className="text-gray-600 mt-1">Create beautiful branded email templates with the visual editor</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePreview} data-testid="preview-email-btn">
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-branding-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-12 gap-4">
        {/* Left Sidebar - Block Palette */}
        <div className="col-span-12 lg:col-span-3">
          <Card className="sticky top-4">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Building Blocks
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <Tabs defaultValue="all" className="space-y-2">
                <TabsList className="w-full grid grid-cols-4 h-8">
                  <TabsTrigger value="all" className="text-xs px-1">All</TabsTrigger>
                  <TabsTrigger value="header" className="text-xs px-1">Header</TabsTrigger>
                  <TabsTrigger value="content" className="text-xs px-1">Content</TabsTrigger>
                  <TabsTrigger value="footer" className="text-xs px-1">Footer</TabsTrigger>
                </TabsList>
                
                  <Droppable droppableId="block-palette" isDropDisabled={true}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-1">
                        <TabsContent value="all" className="mt-0 space-y-1">
                          {Object.values(EMAIL_BLOCKS).map((block, index) => (
                            <Draggable key={block.id} draggableId={`palette-${block.id}`} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`flex items-center gap-2 p-2 rounded border cursor-grab transition-all ${snapshot.isDragging ? 'shadow-lg bg-blue-50 border-blue-300' : 'bg-[#0b2130] hover:bg-white/10'}`}
                                >
                                  <block.icon className="h-4 w-4 text-gray-500" />
                                  <span className="text-xs font-medium">{block.label}</span>
                                  <Badge variant="outline" className="ml-auto text-[10px] px-1">{block.category}</Badge>
                                </div>
                              )}
                            </Draggable>
                          ))}
                        </TabsContent>
                        
                        {['header', 'content', 'footer', 'layout'].map(category => (
                          <TabsContent key={category} value={category} className="mt-0 space-y-1">
                            {Object.values(EMAIL_BLOCKS).filter(b => b.category === category).map((block, index) => (
                              <Draggable key={`${category}-${block.id}`} draggableId={`palette-${block.id}`} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`flex items-center gap-2 p-2 rounded border cursor-grab ${snapshot.isDragging ? 'shadow-lg bg-blue-50' : 'bg-[#0b2130] hover:bg-white/10'}`}
                                  >
                                    <block.icon className="h-4 w-4 text-gray-500" />
                                    <span className="text-xs font-medium">{block.label}</span>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                          </TabsContent>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
              </Tabs>
              
              {/* Quick Templates */}
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                  <Wand2 className="h-3 w-3" /> Quick Templates
                </p>
                <div className="space-y-1">
                  <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8" onClick={() => addPresetTemplate('modern')}>
                    <Sparkles className="h-3 w-3 mr-2 text-blue-500" /> Modern
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8" onClick={() => addPresetTemplate('minimal')}>
                    <Layout className="h-3 w-3 mr-2 text-gray-500" /> Minimal
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8" onClick={() => addPresetTemplate('professional')}>
                    <Mail className="h-3 w-3 mr-2 text-purple-500" /> Professional
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Center - Canvas */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Email Canvas</CardTitle>
                <div className="flex gap-1">
                  <Button 
                    variant={editorMode === 'visual' ? 'default' : 'outline'} 
                    size="sm" 
                    className="h-7 text-xs"
                    onClick={() => setEditorMode('visual')}
                  >
                    <Layout className="h-3 w-3 mr-1" /> Visual
                  </Button>
                  <Button 
                    variant={editorMode === 'code' ? 'default' : 'outline'} 
                    size="sm" 
                    className="h-7 text-xs"
                    onClick={() => setEditorMode('code')}
                  >
                    <Code className="h-3 w-3 mr-1" /> Code
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {editorMode === 'visual' ? (
                <div className="bg-gray-100 p-4 min-h-[500px]">
                  <div className="max-w-[600px] mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
                      <Droppable droppableId="template-canvas">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`min-h-[400px] transition-colors ${snapshot.isDraggingOver ? 'bg-blue-50' : ''}`}
                          >
                            {branding.template_blocks.length === 0 ? (
                              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                                <Plus className="h-12 w-12 mb-3" />
                                <p className="text-sm font-medium">Drag blocks here</p>
                                <p className="text-xs mt-1">or choose a quick template</p>
                              </div>
                            ) : (
                              branding.template_blocks.map((block, index) => (
                                <Draggable key={block.id} draggableId={block.id} index={index}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`relative group ${selectedBlockIndex === index ? 'ring-2 ring-blue-500' : ''} ${snapshot.isDragging ? 'shadow-xl' : ''}`}
                                      onClick={() => setSelectedBlockIndex(index)}
                                    >
                                      {/* Block controls */}
                                      <div className="absolute -left-10 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1">
                                        <div {...provided.dragHandleProps} className="p-1 bg-white rounded shadow cursor-grab hover:bg-gray-100">
                                          <GripVertical className="h-4 w-4 text-gray-400" />
                                        </div>
                                      </div>
                                      
                                      {/* Block content preview */}
                                      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderBlockToHtml(block, branding)) }} />
                                    </div>
                                  )}
                                </Draggable>
                              ))
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                  </div>
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  <div>
                    <Label className="text-xs font-semibold">Header HTML</Label>
                    <Textarea 
                      value={headerHtml} 
                      onChange={(e) => setHeaderHtml(e.target.value)} 
                      rows={8} 
                      className="font-mono text-xs mt-1"
                      placeholder="<div style='...'>Your header HTML</div>"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Footer HTML</Label>
                    <Textarea 
                      value={footerHtml} 
                      onChange={(e) => setFooterHtml(e.target.value)} 
                      rows={8} 
                      className="font-mono text-xs mt-1"
                      placeholder="<div style='...'>Your footer HTML</div>"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar - Block Properties + Settings */}
        <div className="col-span-12 lg:col-span-3">
          <div className="space-y-4 sticky top-4">
            {/* Block Properties */}
            {selectedBlockIndex !== null && branding.template_blocks[selectedBlockIndex] && (
              <Card>
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Block Properties</CardTitle>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveBlock('up')} disabled={selectedBlockIndex === 0}>
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveBlock('down')} disabled={selectedBlockIndex === branding.template_blocks.length - 1}>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-2">
                  <BlockEditor
                    block={branding.template_blocks[selectedBlockIndex]}
                    onUpdate={updateBlock}
                    onDelete={deleteBlock}
                  />
                </CardContent>
              </Card>
            )}

            {/* Brand Settings */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Brand Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Use Custom Branding</Label>
                  <Switch
                    checked={branding.use_custom_branding}
                    onCheckedChange={(checked) => setBranding({...branding, use_custom_branding: checked})}
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Company Name</Label>
                  <Input 
                    value={branding.company_name} 
                    onChange={(e) => setBranding({...branding, company_name: e.target.value})}
                    className="h-8 text-sm mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Primary Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input 
                      type="color" 
                      value={branding.primary_color} 
                      onChange={(e) => setBranding({...branding, primary_color: e.target.value})}
                      className="w-12 h-8 p-1"
                    />
                    <Input 
                      value={branding.primary_color} 
                      onChange={(e) => setBranding({...branding, primary_color: e.target.value})}
                      className="h-8 text-sm flex-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs">Logo URL</Label>
                  <Input 
                    value={branding.company_logo_url} 
                    onChange={(e) => setBranding({...branding, company_logo_url: e.target.value})}
                    className="h-8 text-sm mt-1"
                    placeholder="https://..."
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Support Email</Label>
                  <Input 
                    value={branding.support_email} 
                    onChange={(e) => setBranding({...branding, support_email: e.target.value})}
                    className="h-8 text-sm mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Support Phone</Label>
                  <Input 
                    value={branding.support_phone} 
                    onChange={(e) => setBranding({...branding, support_phone: e.target.value})}
                    className="h-8 text-sm mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Company Address</Label>
                  <Textarea 
                    value={branding.company_address} 
                    onChange={(e) => setBranding({...branding, company_address: e.target.value})}
                    className="text-sm mt-1"
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      </DragDropContext>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Email Preview
            </DialogTitle>
            <DialogDescription>
              Preview how your email will appear to recipients
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-center gap-2 py-2 border-b">
            <Button 
              variant={previewMode === 'desktop' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setPreviewMode('desktop')}
            >
              <Monitor className="h-4 w-4 mr-2" /> Desktop
            </Button>
            <Button 
              variant={previewMode === 'mobile' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setPreviewMode('mobile')}
            >
              <Smartphone className="h-4 w-4 mr-2" /> Mobile
            </Button>
          </div>
          
          {loadingPreview ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
              <p className="ml-3 text-gray-600">Generating preview...</p>
            </div>
          ) : (
            <div className="flex-1 overflow-auto bg-gray-100 p-4">
              <div className={`mx-auto transition-all ${previewMode === 'mobile' ? 'max-w-[375px]' : 'max-w-[700px]'}`}>
                <iframe
                  ref={iframeRef}
                  title="Email Preview"
                  className="w-full bg-white border-0 rounded shadow-lg"
                  style={{ minHeight: '500px', height: '600px' }}
                  srcDoc={previewHtml || ''}
                />
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setPreviewDialogOpen(false);
              handleSave();
            }}>
              <Save className="h-4 w-4 mr-2" />
              Save & Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
